import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Skeleton,
    TextField,
    InputAdornment,
    FormGroup,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { styles } from './styles';
import {
    usePreCreateRolesMappingToChangeRequestPolicyMutation,
    useSaveRolesMappedToChangeRequestPolicyMutation,
} from '../../api/changeRequests';

export const DisplayChangeRequestPolicyRoles = (props) => {
    const { title, isLoading, roles, selectedRoles, handleChange } = props;
    const [searchText, setSearchText] = useState('');

    const filteredRoles = useMemo(() => {
        return roles?.filter((item) => item.label?.toLowerCase()?.search(searchText?.toLowerCase()) >= 0) || [];
    }, [roles, searchText]);

    return (
        <Box>
            <Typography variant="h4">{title}</Typography>
            <Grid item>
                {isLoading ? (
                    <Box pt="10px">
                        <Skeleton height={42} />
                        <Skeleton />
                        <Skeleton />
                    </Box>
                ) : (
                    <Box pt="20px">
                        <Card
                            sx={{
                                boxShadow: 'none',
                            }}
                        >
                            <CardContent>
                                <TextField
                                    id="outlined-basic"
                                    placeholder="Search here"
                                    variant="outlined"
                                    size="small"
                                    value={searchText}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchOutlinedIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                                {filteredRoles?.length === 0 ? (
                                    <Box pt="20px" textAlign="center">
                                        No roles found
                                    </Box>
                                ) : (
                                    <Box pt="20px">
                                        <FormGroup>
                                            {filteredRoles?.map((item) => (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={selectedRoles.includes(item.id)}
                                                            onChange={(e) => handleChange(item.id, e.target.checked)}
                                                        />
                                                    }
                                                    label={item.label}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Grid>
        </Box>
    );
};

const ChangeRequestPolicyRoles = (props) => {
    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState({
        major: [],
        minor: [],
    });

    const { enqueueSnackbar } = useSnackbar();

    const [preCreateRolesMapping, { isLoading: isPreCreateLoading }] =
        usePreCreateRolesMappingToChangeRequestPolicyMutation();
    const [saveRolesMapped, { isLoading: isSaveLoading }] = useSaveRolesMappedToChangeRequestPolicyMutation();

    const isLoading = isPreCreateLoading || isSaveLoading;

    const fetchPreCreateUpdateData = async () => {
        try {
            const response = await preCreateRolesMapping().unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            setRoles(response?.roles || []);
            setSelectedRoles({
                major: response?.majorApproverRoles ? JSON.parse(response?.majorApproverRoles) : [],
                minor: response?.minorApproverRoles ? JSON.parse(response?.minorApproverRoles) : [],
            });
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    useEffect(() => {
        fetchPreCreateUpdateData();
    }, []);

    const updateRoles = async () => {
        const bodyFormData = new FormData();
        bodyFormData.append('majorApproverRoles', selectedRoles.major);
        bodyFormData.append('minorApproverRoles', selectedRoles.minor);
        try {
            const response = await saveRolesMapped(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, {
                variant: 'success',
            });
            fetchPreCreateUpdateData();
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const handleChange = (id, isChecked, type) => {
        let roles = [...selectedRoles[type]];
        if (!isChecked) roles.splice(roles.indexOf(id), 1);
        else roles = [...roles, id];
        setSelectedRoles({ ...selectedRoles, [type]: roles });
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Change Policy Roles</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Grid container direction="column">
                            <Grid container direction="flex" spacing={4} p={3}>
                                <Grid
                                    item
                                    sx={{
                                        width: '50%',
                                    }}
                                    p={2}
                                >
                                    <DisplayChangeRequestPolicyRoles
                                        title="Minor Roles"
                                        isLoading={isLoading}
                                        roles={roles?.map((item) => ({
                                            id: item.id,
                                            label: item.displayName,
                                        }))}
                                        selectedRoles={selectedRoles.minor}
                                        handleChange={(id, isChecked) => handleChange(id, isChecked, 'minor')}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    sx={{
                                        width: '50%',
                                    }}
                                    p={2}
                                >
                                    <DisplayChangeRequestPolicyRoles
                                        title="Major Roles"
                                        isLoading={isLoading}
                                        roles={roles?.map((item) => ({
                                            id: item.id,
                                            label: item.displayName,
                                        }))}
                                        selectedRoles={selectedRoles.major}
                                        handleChange={(id, isChecked) => handleChange(id, isChecked, 'major')}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                sx={{
                                    pt: '0 !important',
                                    display: 'flex',
                                }}
                            >
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                    }}
                                />
                                <LoadingButton
                                    loading={isSaveLoading}
                                    variant="contained"
                                    color="primary"
                                    disabled={selectedRoles.major.length === 0 || selectedRoles.minor.length === 0}
                                    onClick={updateRoles}
                                >
                                    Update
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ChangeRequestPolicyRoles;
